import DataProvider from '@/utils/helpers/DataProvider';
import StoreUtil from '@/utils/helpers/StoreUtil';

import {
  IUser,
  IUserProfile,
  IUserStatus,
  ILikeData,
} from '@/models/interfaces/users';
import { IVuexState } from '@/models/interfaces/store';
import { RootState } from '..';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import { AxiosError } from 'axios';
import { uniq } from 'ramda';

export interface State {
  user: IVuexState<IUser>;
  userProfile: IVuexState<IUserProfile>;
  userStatus: IVuexState<IUserStatus>;
  likedUsersIds: string[];
}

export const getters: GetterTree<State, RootState> = {
  isUserLiked(state) {
    return (id: string) => state.likedUsersIds.includes(id);
  },

  userStatus(state) {
    const userStatus = state.userStatus;

    if (!state.userStatus.data) {
      userStatus.data = {
        online: false,
        domino: false,
        lastSeen: '2000-01-01 00:00',
        friendCount: 0,
        notificationCount: 0,
        notificationLast: '',
        likeCount: 0,
        canBeLiked: false,
      };
    }

    return userStatus;
  },

  userProfile(state) {
    const userProfile = state.userProfile;

    if (!state.userProfile.data) {
      const noModel = { model: 'none', styles: { colour: '' } };

      userProfile.data = {
        wallpaper: {
          id: 'none',
          imgUrl: '',
          bgColor: '#e1ecf8',
          gradColor: '#e1ecf8',
        },
        interests: {
          bgColor: 'linear-gradient(to top, #00ffff, #ff00ff)',
          gradColor: '#ff00ff',
          items: [
            {
              id: 'interest_17',
              imgUrl:
                'https://cdn.littlebridge.com/ck/img/common/wallpapers/interest_17.png',
            },
            {
              id: 'interest_13',
              imgUrl:
                'https://cdn.littlebridge.com/ck/img/common/wallpapers/interest_13.png',
            },
            {
              id: 'interest_4',
              imgUrl:
                'https://cdn.littlebridge.com/ck/img/common/wallpapers/interest_4.png',
            },
          ],
        },
        avatar: {
          body: { model: 'body', styles: { colour: '#e1ecf8' } },
          eyes: { model: 'eyes', styles: { colour: '#e1ecf8' } },
          eyelids: { model: 'eyelids-human', styles: { colour: '' } },
          head: { model: 'head', styles: { colour: '#e1ecf8' } },
          glasses: noModel,
          hair: { model: 'hair-mid-spikey', styles: { colour: '#e1ecf8' } },
          hat: noModel,
          badge: noModel,
          top: { model: 'top-tshirt', styles: { colour: '#e1ecf8' } },
          topTexture: noModel,
          bottom: { model: 'bottom-trousers', styles: { colour: '#e1ecf8' } },
          shoes: { model: 'shoes-deck', styles: { colour: '#e1ecf8' } },
          socks: noModel,
          accessories: noModel,
        },
        domino: {
          body: noModel,
          hair: noModel,
          head: noModel,
          eyes: noModel,
          eyelids: noModel,
          collar: noModel,
          top: noModel,
        },
        friendShipStatus: null,
        avatarImage: null,
      };
    }

    return userProfile;
  },
};

export const mutations: MutationTree<State> = {
  setUser(state, data?: IUser | Error) {
    state.user = StoreUtil.updateState(state.user, data);
  },
  setUserProfile(state, data?: IUserProfile | Error) {
    state.userProfile = StoreUtil.updateState(state.userProfile, data);
  },
  setUserStatus(state, data?: IUserStatus | Error) {
    state.userStatus = StoreUtil.updateState(state.userStatus, data);
  },
  addLikedUserId(state, id: string) {
    state.likedUsersIds = uniq([...state.likedUsersIds, id]);
  },
};

export const actions: ActionTree<State, RootState> = {
  async fetchUser({ commit }, userId: string) {
    commit('setUser');

    try {
      const { data } = await DataProvider.get(`users/${userId}`);
      commit('setUser', data);
      return data;
    } catch (error) {
      commit('setUser', error);
      return error;
    }
  },
  async fetchUserProfile({ commit }, userId: string): Promise<IUserProfile> {
    commit('setUserProfile');

    try {
      const { data } = await DataProvider.get<IUserProfile>(
        `users/${userId}/profile`,
      );
      commit('setUserProfile', data);
      return data;
    } catch (error) {
      commit('setUserProfile', error);
      // @ts-ignore
      return error;
    }
  },
  async fetchUserStatus({ commit }, userId: string): Promise<IUserStatus> {
    commit('setUserStatus');

    try {
      const { data } = await DataProvider.get<IUserStatus>(
        `users/${userId}/status`,
      );
      commit('setUserStatus', data);
      return data;
    } catch (error) {
      commit('setUserStatus', error);
      return error as IUserStatus;
    }
  },
  async updateUserProfile(
    { commit },
    {
      userId,
      payload,
      mutateOnRequest = true,
    }: {
      userId: string;
      payload: Partial<IUserProfile>;
      mutateOnRequest: boolean;
    },
  ) {
    const mutate = (payload?: Partial<IUserProfile> | AxiosError) => {
      commit('profile/setProfile', payload, { root: true });
      commit('setUserProfile', payload);
    };

    if (mutateOnRequest) mutate(payload);

    try {
      const { data } = await DataProvider.put(
        `users/${userId}/profile`,
        payload,
      );

      if (mutateOnRequest) mutate(payload);
      return data;
    } catch (error) {
      // @ts-ignore
      mutate(error);
      return error;
    }
  },
  async likeUserProfile({ commit }, data: ILikeData) {
    try {
      const { data: response } = await DataProvider.post('likes', data);
      commit('addLikedUserId', data.likeUserId);
      return response;
    } catch (error) {
      return error;
    }
  },
  async saveProfileImage(
    { commit },
    data: { userId: string; avSnapshot: Blob },
  ) {
    try {
      const blobToBase64 = function blobToBase64(blob: Blob) {
        return new Promise((resolve, _) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      };

      const readerData = await blobToBase64(data.avSnapshot);
      // Remove prefix
      const base64Data: string = (readerData as string).split(',')[1];

      const { data: response } = await DataProvider.put(
        `users/${data.userId}/profileImage`,
        {
          data: base64Data,
        },
      );

      return response;
    } catch (error) {
      return error;
    }
  },
};

const store: Module<State, RootState> = {
  namespaced: true,
  mutations,
  actions,
  getters,
  state: {
    user: StoreUtil.state(),
    userProfile: StoreUtil.state(),
    userStatus: StoreUtil.state(),
    likedUsersIds: [],
  },
};

export default store;
